import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FONTE_TEXTO } from '../paleta';
import { definirForcarSemCache, ehQueryDoOrquestrador } from '../utils/cacheTempoReal';

/**
 * "Atualizar agora" — busca os dados ignorando o cache do servidor.
 *
 * POR QUE EXISTE (05/10, pedido do dono): *"precisamos ir validando algumas
 * coisas praticamente em tempo real"*. O TTL do BFF caiu de 5 min para 5s, o que
 * já resolve o caso comum (recarregou, viu o dado novo) — este botão é para
 * quem não quer esperar nem esses 5s, tipicamente logo depois de um deploy.
 *
 * COMO: marca a janela com `__orqNoCache`, que a camada de API lê para mandar
 * `?nocache=1` na próxima rodada, e então invalida TODAS as queries do
 * orquestrador (prefixo `orq`) para que refaçam a busca. O flag é limpo no fim,
 * de modo que o clique force UMA rodada e não todas as seguintes — senão o
 * cache deixaria de existir na prática.
 *
 * O backend trata `?nocache=1` pulando a LEITURA mas mantendo a GRAVAÇÃO: quem
 * clica paga a agregação e o resultado fresco fica para todos os outros.
 */

export const BotaoAtualizarAgora: React.FC = () => {
  const queryClient = useQueryClient();
  const [buscando, setBuscando] = useState(false);

  const atualizar = async () => {
    // Sem `if (buscando) return` aqui: o `disabled={buscando}` do próprio botão
    // já impede o segundo clique, e a mutação confirmou que a guarda extra não
    // mudava resultado nenhum — era código morto.
    setBuscando(true);
    definirForcarSemCache(true);
    try {
      // `refetchQueries`, não `invalidateQueries`: o invalidate só marca como
      // obsoleto e a busca pode acontecer DEPOIS de o flag ser limpo, perdendo
      // o `?nocache=1`. O refetch dispara agora e dá para esperar.
      await queryClient.refetchQueries({
        predicate: q => ehQueryDoOrquestrador(q.queryKey),
      });
    } catch {
      // Falha de rede aqui NÃO é erro deste botão: cada query tem o seu próprio
      // tratamento de erro e a tela já mostra o estado delas. Engolir evita uma
      // "unhandled rejection" no console do usuário por algo que a tela já
      // comunica — o `finally` abaixo é quem garante o que importa.
    } finally {
      definirForcarSemCache(false);
      setBuscando(false);
    }
  };

  return (
    <button
      type="button"
      onClick={atualizar}
      disabled={buscando}
      aria-label="Atualizar agora, ignorando o cache do servidor"
      title="Busca os dados agora, sem esperar o cache do servidor expirar"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 12px',
        border: '1px solid oklch(0.9 0.006 160)',
        borderRadius: '8px',
        background: 'oklch(1 0 0)',
        fontFamily: FONTE_TEXTO,
        fontSize: '12.5px',
        fontWeight: 600,
        color: buscando ? 'oklch(0.65 0.01 165)' : 'oklch(0.35 0.02 165)',
        cursor: buscando ? 'progress' : 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      <span aria-hidden="true">↻</span>
      {buscando ? 'Atualizando…' : 'Atualizar agora'}
    </button>
  );
};
